// P3-2 — A REINSTALL MUST NOT SILENTLY END HER NOTIFICATIONS.
//
// **MICHAELA'S CASE, AND IT IS A CLASS RATHER THAN AN ACCIDENT.** She deleted the installed app,
// reinstalled it, signed into the same account — and stopped receiving push. Bell rows kept landing,
// because those are database rows; the phone went quiet, because a reinstall destroys the browser's
// push subscription and mints nothing to replace it.
//
// The trap is in the ask logic and it is a one-line reading of stale state: `shouldOfferPush` does
// not offer when a prior answer of `granted` is on file. That is exactly right for a member who is
// already subscribed and exactly wrong for one whose subscription no longer exists — **so the state
// that proves she said yes is the same state that prevents her ever being asked again.** No error,
// no log, nothing on her screen: the most expensive shape this house keeps finding.
//
// THE FIX IS TO STOP TRUSTING THE STORED ANSWER ON ITS OWN AND ASK THE BROWSER. The account's grant
// and the live registration are two different facts, and only their COMBINATION says what to do:
//
//   granted + a live subscription        -> re-post it (cheap, idempotent, heals a lost server row)
//   granted + NO live subscription + permission still granted -> SUBSCRIBE SILENTLY. iOS often keeps
//                                           the notification permission across a reinstall, and where
//                                           it does, no gesture is needed and she should never see a
//                                           prompt for something she already agreed to.
//   granted + NO live subscription + permission gone          -> the grant is STALE. Clear it back to
//                                           "later" so the ask can legitimately return.
//
// Pure and importing nothing, so `scripts/p32-push-heal.mjs` RUNS it — the entry requirement every
// law in `src/` is held to.

export const HEAL = {
  REPOST: "repost-live-subscription",
  RESUBSCRIBE: "resubscribe-silently",
  REASK: "clear-stale-grant-and-ask",
  NOTHING: "nothing-to-do",
};

// **THE FIRST VERSION OF THIS FILE FIXED ONE DEAD END BY WRITING ANOTHER, AND HER DEVICE PROVED IT
// IN ONE LAUNCH.** The REASK path recorded the answer as `later`. But `shouldOfferPush` refuses to
// offer when there is ANY prior answer on file — `later` included, deliberately, because "not now"
// is meant to stop the nag. So clearing a stale grant into `later` moved her from one permanently
// silent state to another, and the ask could never return. Her receipts: `push_answer` went to
// `later` at 04:57:33 (the heal ran, correctly detecting a dead subscription and a revoked
// permission), and no ask ever appeared.
//
// **A STALE GRANT IS NOT AN ANSWER — IT IS THE ABSENCE OF ONE.** She never answered for THIS
// installation; the previous install's answer died with it. So the heal CLEARS the field rather than
// writing a value, and the ask returns because there is genuinely nothing on file. That is the same
// distinction R18/R20 keep finding: a value deliberately recorded is an answer, and an absent one is
// not, and collapsing them is how a member ends up in silence nobody can see.
export const CLEAR_ANSWER = null;

// **iOS REVOKES THE NOTIFICATION PERMISSION WHEN THE PWA IS DELETED — measured on her device, not
// assumed.** The heal observed `permission !== "granted"` after her re-add, which is the only way it
// could have reached the REASK branch. So the silent re-subscribe path is the rarer one on iOS and
// the ask-returns path is the common one, which makes the ask actually returning load-bearing.
export const IOS_DELETE_REVOKES_PERMISSION = true;

// `answered` is what the ACCOUNT says. `hasLiveSubscription` is what the BROWSER says. `permission`
// is what the OS says. A decision made from fewer than all three is the bug this file exists for.
// DT-2a — **THE FOURTH FACT, AND IT IS THE ONLY ONE THE DEVICE CANNOT KNOW.**
//
// The three facts above can ALL say everything is fine while the endpoint is a corpse the push
// service is 201-ing. That is `worker-silent` in DT-1c's roster — accepted by the service, never
// answered by the device — and it is the exact state the three probes were sent to diagnose. In it:
// the account is `granted`, the browser still holds a subscription object, the OS permission is
// still granted, so `healDecision` answers REPOST and **re-posts the same dead endpoint, on every
// launch, forever.** Nothing on the device can tell, because nothing on the device is what went
// wrong. **Only the beacon knows**, which is the whole reason DT-1 exists.
//
// So the house's own verdict is a fourth input. **IT IS THE SERVER'S FACT AND IT ARRIVES FROM THE
// SERVER**, which is OR-4's ruling in this very file, word for word: a `{ heal: true }` flag from
// the client was the obvious repair and the wrong one, because then the caller decides. It rides on
// `/api/push/key`, which the heal already fetches and already reads `answered` off, so there is no
// new round trip and no new route.
//
// **THE TRIGGER IS `worker-silent` ALONE AND THE NARROWNESS IS THE RULING.** It is the one cause
// that means the endpoint we hold is accepted and dead: `endpoint-gone` has no device row left to
// re-mint, `grant-gone` needs the ask rather than a silent mint, `never-accepted` is the service
// refusing US, and `unknown` is evidence that cannot say (R18/R19/R141 — refuse, never assume). A
// wider trigger would re-mint every member's endpoint on every launch the day the tag crossing
// drifts, because a broken crossing reports silence for the whole roster at once (DT-3a).
export const HOUSE_SILENT_CAUSE = "worker-silent";
export const houseSaysEndpointDead = (cause) => String(cause || "") === HOUSE_SILENT_CAUSE;

export function healDecision({ answered = null, hasLiveSubscription = false, permission = "default", installed = true, supported = true, houseCause = "" } = {}) {
  if (!installed || !supported) return HEAL.NOTHING;
  // Only a GRANTED account can be in the broken state: a member who said later or no is handled by
  // the ordinary ask, and re-minting for her would be subscribing somebody who never agreed.
  if (answered !== "granted") return HEAL.NOTHING;
  if (hasLiveSubscription) {
    // **THE HOUSE OVERRULES A LIVE-LOOKING SUBSCRIPTION, AND ONLY EVER TOWARDS THE SILENT REPAIR.**
    // Re-minting needs the OS permission it already has, so this branch is exactly as prompt-free as
    // the RESUBSCRIBE path below — she is never asked for something she already agreed to. Without
    // the permission there is nothing to mint with, so the honest answer is the ask, not a mint that
    // would fail.
    if (houseSaysEndpointDead(houseCause)) {
      return permission === "granted" ? HEAL.RESUBSCRIBE : HEAL.REASK;
    }
    return HEAL.REPOST;
  }
  if (permission === "granted") return HEAL.RESUBSCRIBE;
  return HEAL.REASK;
}

// **THE HEAL RUNS ON EVERY LAUNCH OF THE INSTALLED APP**, not once per session and not behind a
// button. A member who reinstalls does not know anything is wrong — there is nothing on her screen
// to tap — so the only moment that can fix it is the one she gives us by opening the app.
export function shouldRunHeal({ installed = false, signedIn = false, supported = false } = {}) {
  return Boolean(installed && signedIn && supported);
}

// ---------------------------------------------------------------------------
// OR-4 — **THE WELCOME PUSH FIRED ON EVERY APP OPEN, AND THE HEAL ABOVE IS WHY.**
//
// Her report, and the suspect she named is the right one. `shouldRunHeal` runs on EVERY launch of
// the installed app, by design; the REPOST branch re-posts her live subscription to
// `/api/push/subscribe`, by design and cheaply; and that route sent the welcome line unconditionally.
// So a member who opened the app four times on a Tuesday was told four times that notifications are
// on. **Every piece of that was correct on its own** — which is why it survived: the defect only
// exists at the seam, and no fixture stood there.
//
// **THE DECISION IS THE SERVER'S AND IT IS TAKEN FROM A FACT THE SERVER OWNS.** A `{ heal: true }`
// flag from the client was the obvious repair and is the wrong one: the caller would then decide
// whether her phone buzzes, and the next call site to forget it announces again (R27/R33 — the
// receiving side's default must never be the loud one). The fact is whether this ENDPOINT IS NEW:
// a heal re-posts an endpoint we already hold, and a grant brings one we do not.
//
// **"ONCE PER GRANT, EVER" IS READ AT THE DEVICE**, and that is a ruling rather than an obvious
// reading. Her account-level answer is already `granted` when she adds a second phone, so keying on
// the account would send no welcome on the device she just set up — and the welcome's whole job is
// being the one honest proof the chain works ON THAT DEVICE (keys, subscription, worker and all).
// Keyed at the endpoint: the first grant buzzes, a second device buzzes once, a reinstall that
// re-mints buzzes once, and ten launches buzz once between them, which is the item.
// **AN ABSENT ANSWER IS NOT A NEW DEVICE.** A write that came back `null` (the store refused, the
// database was unreachable) must not buzz — and it must not THROW either, because the caller here is
// a fire-and-forget line after the response has already gone (R19: a law answers, it does not end
// the run).
export function shouldWelcome(saved) {
  return Boolean(saved && saved.created);
}

// What a re-mint means for the rows already on file. A reinstall leaves the OLD row looking healthy
// (it has never failed — nothing has been sent to it since), so it cannot be found by failure alone.
// It is superseded the moment a new endpoint arrives for the same device.
export function staleRowsAfterRemint(rows = [], { endpoint = "", userAgent = "" } = {}) {
  const fresh = String(endpoint || "");
  return rows
    .filter((r) => String(r.endpoint || "") !== fresh)
    // SAME DEVICE, NEW ENDPOINT. A member with a phone AND a tablet keeps both, which is why this is
    // not "delete everything else" — that would cost her a device every time she opened the other one.
    .filter((r) => String(r.userAgent || r.user_agent || "") === String(userAgent || ""))
    .map((r) => r.endpoint);
}
